
import React, { useState } from 'react';
import { Card } from "@/components/ui/card";
import type { Flashcard } from '@/types/flashcard';
import FlashcardFront from './FlashcardFront';
import FlashcardBack from './FlashcardBack';
import { Button } from "@/components/ui/button";
import { ThumbsUp, ThumbsDown, ArrowRight, RotateCw } from "lucide-react";
import { toast } from "sonner";

interface FlashcardViewProps {
  card: Flashcard;
  onConfidenceRating: (rating: number) => void;
  onNext: () => void;
}

const FlashcardView: React.FC<FlashcardViewProps> = ({
  card,
  onConfidenceRating,
  onNext,
}) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [hasRated, setHasRated] = useState(false);

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleRating = (rating: number) => {
    onConfidenceRating(rating);
    setHasRated(true);
    toast.success(`Rating recorded: ${rating === 0 ? 'Again' : rating === 0.5 ? 'Hard' : 'Easy'}`);
  };

  const handleNext = () => {
    onNext();
    setIsFlipped(false);
    setHasRated(false);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card 
        className={`relative h-64 sm:h-80 transition-shadow duration-200 cursor-pointer perspective-1000 bg-gradient-to-br from-white to-purple-50 dark:from-gray-900 dark:to-purple-900/20 backdrop-blur-sm border border-purple-100/50 dark:border-purple-500/10 shadow-lg hover:shadow-xl ${
          isFlipped ? 'flashcard-flipped' : ''
        }`}
        onClick={handleFlip}
      >
        {/* Front of card */}
        <div className={`absolute w-full h-full backface-hidden transition-transform duration-500 ${
          isFlipped ? 'rotate-y-180 pointer-events-none' : 'rotate-y-0'
        }`}>
          <FlashcardFront 
            content={card.front}
            onFlip={handleFlip}
          />
        </div>

        {/* Back of card */}
        <div className={`absolute w-full h-full backface-hidden transition-transform duration-500 ${
          isFlipped ? 'rotate-y-0' : 'rotate-y-180 pointer-events-none'
        }`}>
          <FlashcardBack 
            content={card.back}
            onFlip={handleFlip}
          />
        </div>
      </Card>

      <div className="pt-4 mt-4 border-t border-purple-100 dark:border-purple-800">
        {isFlipped && !hasRated && (
          <>
            <p className="text-sm text-center mb-3 text-purple-700 dark:text-purple-300">How well did you know this?</p>
            <div className="flex justify-center gap-2">
              <Button 
                className="flex-1 px-3 py-2 bg-gradient-to-r from-secondary-300 to-secondary-400 text-white hover:from-secondary-400 hover:to-secondary-500 border border-secondary-500/20 transition-all"
                onClick={() => handleRating(0)}
              >
                <RotateCw className="h-4 w-4 mr-2" />
                Again
              </Button>
              <Button 
                className="flex-1 px-3 py-2 bg-gradient-to-r from-primary-300 to-primary-400 text-white hover:from-primary-400 hover:to-primary-500 border border-primary-500/20 transition-all"
                onClick={() => handleRating(0.5)}
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                Hard
              </Button>
              <Button 
                className="flex-1 px-3 py-2 bg-gradient-to-r from-accent-400 to-accent-500 text-white hover:from-accent-500 hover:to-accent-600 border border-accent-500/20 transition-all"
                onClick={() => handleRating(1)}
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Easy
              </Button>
            </div>
          </>
        )}

        {isFlipped && hasRated && (
          <div className="flex justify-center">
            <Button 
              className="bg-gradient-to-r from-exam-purple to-secondary-500 hover:from-exam-purple hover:to-secondary-600 text-white px-6 shadow-md hover:shadow-lg transition-shadow"
              onClick={handleNext}
            >
              Next Card
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}

        {!isFlipped && (
          <div className="text-center">
            <Button 
              className="px-4 py-2 bg-gradient-to-r from-exam-purple-light to-secondary-100 text-secondary-600 hover:from-exam-purple-light hover:to-secondary-200 transition-colors duration-200 flex items-center mx-auto shadow-sm hover:shadow-md"
              onClick={handleFlip}
            >
              Show Answer
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FlashcardView;
